import React from "react";
import { useNavigate } from "react-router-dom";
import PublicHeader from "../components/homepage/public/PublicHeader";
import PublicFooter from "../components/homepage/public/PublicFooter";

const styles = {
  page: {
    fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    background: "#fff",
    color: "#0f172a",
  },
  container: { maxWidth: "1100px", margin: "0 auto", padding: "0 20px" },
  hero: {
    padding: "54px 0",
    background: "radial-gradient(circle at top, #f2f4fa 0, #ffffff 62%)",
    borderBottom: "1px solid #e2e8f0",
  },
  heroTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "42px",
    lineHeight: 1.06,
    margin: 0,
    color: "#020617",
  },
  heroText: { marginTop: "14px", fontSize: "15px", color: "#475569", lineHeight: 1.9 },
  heroActions: { marginTop: "18px", display: "flex", gap: "10px", flexWrap: "wrap" },
  btn: {
    fontSize: "13px",
    padding: "10px 18px",
    borderRadius: "999px",
    border: "1px solid #d0d7e2",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: 700,
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #1d4ed8, #0b1530)",
    color: "#fff",
    borderColor: "transparent",
    boxShadow: "0 10px 25px rgba(15,23,42,0.14)",
  },
  section: { padding: "70px 0" },
  sectionAlt: {
    padding: "70px 0",
    background: "#f8fafc",
    borderTop: "1px solid #e2e8f0",
    borderBottom: "1px solid #e2e8f0",
  },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: "28px", margin: "0 0 10px", color: "#020617" },
  p: { fontSize: "14px", color: "#64748b", lineHeight: 1.9, margin: 0 },
  contactGrid: {
    marginTop: "26px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "24px",
  },
  contactCard: {
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    padding: "24px",
    boxShadow: "0 16px 40px rgba(15,23,42,0.06)",
  },
  contactTitle: { fontWeight: 800, margin: 0, fontSize: "16px", color: "#0f172a" },
  contactText: { marginTop: "8px", fontSize: "14px", color: "#64748b", lineHeight: 1.7 },
  contactLink: {
    display: "inline-block",
    marginTop: "8px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#1d4ed8",
    textDecoration: "none",
  },
  mapLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "12px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#1d4ed8",
    textDecoration: "none",
  },
};

const MAP_URL = "https://maps.app.goo.gl/pDQFVVqTHJ5wranW7";
const EMAIL = "cumming@trueeducation.com";
const PHONE = "(470) 470-4715";
const ADDRESS = "580 Peachtree Pkwy #102";
const CITY = "Cumming, GA 30041";

export default function ContactPage() {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <PublicHeader />

      <section style={styles.hero}>
        <div style={styles.container}>
          <h1 style={styles.heroTitle}>Contact Us</h1>
          <p style={styles.heroText}>
            Have a question or ready to schedule a free consultation? Call, email, or stop by. We’re
            here to help families in Cumming and the greater Atlanta area find the right path.
          </p>
          <div style={styles.heroActions}>
            <a href={`tel:${PHONE.replace(/\D/g, "")}`} style={{ ...styles.btn, ...styles.btnPrimary, textDecoration: "none", color: "#fff" }}>
              Call (470) 470-4715
            </a>
            <a href={`mailto:${EMAIL}`} style={{ ...styles.btn, textDecoration: "none", color: "inherit" }}>
              Email Us
            </a>
            <button style={styles.btn} onClick={() => navigate("/admissions")}>
              Admissions
            </button>
          </div>
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.container}>
          <h2 style={styles.h2}>Get in Touch</h2>
          <p style={styles.p}>
            Whether you’re interested in SAT or ACT prep, subject tutoring, or a free consultation,
            we’d love to hear from you.
          </p>
          <div style={styles.contactGrid}>
            <div style={styles.contactCard}>
              <h3 style={styles.contactTitle}>Phone</h3>
              <p style={styles.contactText}>Call us to ask questions or schedule a visit.</p>
              <a href={`tel:${PHONE.replace(/\D/g, "")}`} style={styles.contactLink}>
                {PHONE}
              </a>
            </div>
            <div style={styles.contactCard}>
              <h3 style={styles.contactTitle}>Email</h3>
              <p style={styles.contactText}>Send us a message anytime.</p>
              <a href={`mailto:${EMAIL}`} style={styles.contactLink}>
                {EMAIL}
              </a>
            </div>
            <div style={styles.contactCard}>
              <h3 style={styles.contactTitle}>Location</h3>
              <p style={styles.contactText}>
                {ADDRESS}<br />{CITY}
              </p>
              <a
                href={MAP_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.mapLink}
              >
                View on map →
              </a>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.sectionAlt}>
        <div style={styles.container}>
          <h2 style={styles.h2}>What You Can Ask About</h2>
          <p style={styles.p}>
            Free consultations, SAT/ACT programs, diagnostic assessments, one-on-one tutoring,
            scheduling, and pricing. We’ll help you figure out the best next step.
          </p>
          <div style={styles.heroActions}>
            <button
              style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={() => navigate("/tutoring/sat")}
            >
              Explore SAT Tutoring
            </button>
            <button style={styles.btn} onClick={() => navigate("/admissions")}>
              How to Get Started
            </button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
